import { Auth } from "aws-amplify";
import React, { Component, FormEvent, FunctionComponent } from "react";
import { Form, Alert, Card, ListGroup, Row, Col, Spinner } from "react-bootstrap";
import { RouteComponentProps } from "react-router-dom";
import { AssignUserToShopRequestInput, AssignUserToShopRequestOutput, GetShopContactDetailsInput, GetShopContactDetailsOutput, GetShopSummaryInput, GetShopSummaryOutput, IAssignUserShopRequest, IGetShopContactDetails, IRegisterShop, RegisterShopInput, RegisterShopOutput, ShopSummary } from "../../../client/core";
import { Color, Path, Storage } from "../../../env";
import { Button, variant } from "../../form/Button";
import {
    Image,
} from "react-bootstrap";
import { TbSquareNumber1, TbSquareNumber2, TbSquareNumber3, TbSquareNumber4, TbSquareNumber5 } from "react-icons/tb";

export interface RegisterShopState {
    isLoading: boolean;
    error: string;
    state: State;
    shop?: ShopSummary;
}

export interface RegisterShopProps extends RouteComponentProps {
    getShopContactDetails: (input: GetShopContactDetailsInput) => Promise<GetShopContactDetailsOutput | Error>
    assignUserToShopRequest: (input: AssignUserToShopRequestInput) => Promise<AssignUserToShopRequestOutput | Error>
    registerShop: (input: RegisterShopInput) => Promise<RegisterShopOutput>
    getShop: (input: GetShopSummaryInput) => Promise<GetShopSummaryOutput>
    auth: typeof Auth;
}

export enum State {
    None,
    ShopConfirmed,
    ConfirmShop,
}

export default class RegisterShop extends Component<
    RegisterShopProps,
    RegisterShopState
> {
    constructor(props: RegisterShopProps) {
        super(props);
        this.state = {
            isLoading: true,
            state: State.None,
            error: "",
        };
    }

    componentDidMount(): void {
        const shopIdToCreate = localStorage.getItem(Storage.OpenShop)
        if (shopIdToCreate == null) {
            return this.setState({
                isLoading: false,
            })
        }

        this.props.getShopContactDetails({
            shopId: shopIdToCreate
        }).then(response => {
            if (response instanceof Error || !response.body) {
                return this.setState({
                    isLoading: false,
                })
            }

            localStorage.setItem(Storage.OpenShop, response.body.id)

            this.setState({
                isLoading: false,
                state: State.ConfirmShop,
                shop: {
                    name: response.body.name,
                    email: response.body.email,
                    address: response.body.address,
                    phone: response.body.phone,
                    accountStatus: "",
                    actions: [],
                    active: false,
                    alias: "",
                    color: "",
                    credit: 0,
                    deliveryStatus: "",
                    logo: response.body.logo || "",
                }
            })
        })
    }

    handleSubmit = () => {
        this.props.auth
            .currentSession()
            .then((session) => {
                const token = session.getIdToken().getJwtToken();
                this.props.registerShop({
                    identityToken: token,
                    referral: localStorage.getItem(Storage.Referral) || undefined
                }).then((output) => {
                    if (output.statusCode === 403) {
                        return this.props.history.push(Path.Login);
                    }

                    if (output.statusCode === 201) {
                        localStorage.setItem(Storage.ShopID, output.shopID);
                        return this.props.history.push(Path.RegisterShopName);
                    }

                    this.setState({
                        error: "Oop, something unexpected went wrong during the registration. We will investigate why there was an error and get back to you.",
                    });
                }).catch(() =>
                    this.setState({
                        error: "Oop, something unexpected went wrong during the registration. We will investigate why there was an error and get back to you.",
                    })
                );
            })
            .catch(() => {
                this.props.history.push(Path.Login);
            });
    };

    handleAssignUserToShop = () => {
        const shopIdToAssignUser = localStorage.getItem(Storage.OpenShop)
        if (shopIdToAssignUser == null) {
            return
        }

        localStorage.setItem(Storage.ShopID, shopIdToAssignUser)



        this.props.auth.currentSession().then(session => {
            this.props.assignUserToShopRequest({
                identityToken: session.getIdToken().getJwtToken(),
                shopId: shopIdToAssignUser
            }).then(response => {
                if (response instanceof Error || response.statusCode !== 200) {
                    return this.setState({
                        state: State.ConfirmShop,
                        error: "We were unable to allocate you to this shop at this time. Please contact support@stumbled.online if this issue persists."
                    })
                }

                // Check if user was auto-assigned and navigate directly to RegisterShopAccount
                if (response.body?.autoAssigned) {
                    return this.props.history.push(Path.RegisterShopAccount)
                }

                this.setState({
                    state: State.ShopConfirmed
                })
            })
        }).catch(err => {
            localStorage.removeItem(Storage.IsLoggedIn)
            this.props.auth.signOut()
            this.props.history.push(Path.Login)
        })
    }

    handleAddUserConfirmation = () => {
        const shopID = localStorage.getItem(Storage.OpenShop)
        this.props.auth.currentSession().then(async (session) => {
            const token = session.getIdToken().getJwtToken();
            this.props.getShop({
                identityToken: token,
                shopID: shopID!,
                addressID: localStorage.getItem(Storage.ShopAddressID)
            }).then((output) => {
                if (output.statusCode === 403) {
                    console.log(403)
                    return this.setState({
                        error: `To finish assigning yourself to this shop, please open the email sent to ${this.state.shop?.email} and follow the link inside. If you want to book a meeting using the button "Book a meeting" button we'll be happy to assist you.`
                    })
                }

                this.props.history.push(Path.RegisterShopAccount)
            }).catch((err) => {
                this.setState({
                    error: `To finish assigning yourself to this shop, please open the email sent to ${this.state.shop?.email} and follow the link inside. If you want to book a meeting using the button "Book a meeting" button we'll be happy to assist you.`
                });
            });
        }).catch(() => {
            return this.setState({
                error: `To finish assigning yourself to this shop, please open the email sent to ${this.state.shop?.email} and follow the link inside. If you want to book a meeting using the button "Book a meeting" button we'll be happy to assist you.`
            })
        })
    }

    render() {
        if (this.state.isLoading) {
            return <div style={{ textAlign: "center" }}><Spinner variant="success" animation="border" color={Color.Primary} /></div>
        }

        const props: RegisterShopFormProps = {
            error: this.state.error,
            shop: this.state.shop,
            state: this.state.state,
            onSubmit: this.handleSubmit,
            onConfirmUserAssignment: this.handleAddUserConfirmation,
            onAssignUserToShop: this.handleAssignUserToShop,
            onChangeActivity: state => this.setState({ state: state })
        };
        return <RegisterShopForm {...props} />;
    }
}

export interface RegisterShopFormProps {
    error: string;
    shop?: ShopSummary;
    state: State;
    onSubmit: () => void;
    onConfirmUserAssignment: () => void;
    onAssignUserToShop: () => void;
    onChangeActivity: (activity: State) => void;
}

export const RegisterShopForm: FunctionComponent<RegisterShopFormProps> = (
    props
) => (
    <Form>
        <Alert variant={"danger"} show={props.error != ""}>
            {props.error}
        </Alert>
        {(props.state === State.ShopConfirmed || props.state === State.ConfirmShop) && (
            <>
                <h1 style={{ marginBottom: "2rem" }}>Is this your shop?</h1>
                <div className={"elevate"} style={{ width: "250px", textAlign: "center", margin: "auto", border: "1px solid lightgrey" }}>
                    <Image style={{ display: "block", padding: "1rem" }} width="100%" src={props.shop?.logo || "https://via.placeholder.com/350x350.png?text=Logo"} rounded />
                </div>
                <ListGroup>
                    <ListGroup.Item
                        as="li"
                        className="d-flex justify-content-between align-items-start"
                        style={{ border: 0, margin: 0, padding: "1rem 0 0 0" }}
                    >
                        <div className="ms-2 me-auto">
                            <div className="fw-bold">Name</div>
                            {props.shop?.name || ""}
                        </div>
                    </ListGroup.Item>
                </ListGroup>
                <ListGroup>
                    <ListGroup.Item
                        as="li"
                        className="d-flex justify-content-between align-items-start"
                        style={{ border: 0, margin: 0, padding: "1rem 0 0 0" }}
                    >
                        <div className="ms-2 me-auto">
                            <div className="fw-bold">Address</div>
                            {props.shop?.address || ""}
                        </div>
                    </ListGroup.Item>
                </ListGroup>
                <ListGroup>
                    <ListGroup.Item
                        as="li"
                        className="d-flex justify-content-between align-items-start"
                        style={{ border: 0, margin: 0, padding: "1rem 0 0 0" }}
                    >
                        <div className="ms-2 me-auto">
                            <div className="fw-bold">Email</div>
                            {props.shop?.email || ""}
                        </div>
                    </ListGroup.Item>
                </ListGroup>
                <ListGroup>
                    <ListGroup.Item
                        as="li"
                        className="d-flex justify-content-between align-items-start"
                        style={{ border: 0, margin: 0, padding: "1rem 0 0 0" }}
                    >
                        <div className="ms-2 me-auto">
                            <div className="fw-bold">Phone</div>
                            {props.shop?.phone || "-"}
                        </div>
                    </ListGroup.Item>
                </ListGroup>
                {(props.state === State.ConfirmShop) && (
                    <>
                        <Button
                            style={{ width: "100%", marginTop: "2rem" }}
                            buttonvariant={variant.Primary}
                            name={"Yes, that's my shop"}
                            onClick={() => props.onAssignUserToShop()}
                        />
                        <Button
                            style={{ width: "100%", marginTop: "1rem" }}
                            buttonvariant={variant.Secondary}
                            name={"No, I want to register a different one"}
                            onClick={() => props.onSubmit()}
                        />
                    </>
                )}
                {(props.state === State.ShopConfirmed) && (
                    <div>
                        <p style={{ borderLeft: `.5rem solid ${Color.Primary}`, padding: ".5rem", margin: ".5rem" }}>Great! We’ve emailed the shop to confirm your request. Look out for an ‘Add User Request’ email sent to <span style={{ color: Color.Primary, fontWeight: 800 }}>{props.shop?.email}</span></p>
                        <Button
                            style={{ width: "100%", marginTop: "1rem" }}
                            buttonvariant={variant.Primary}
                            name={"Email confirmed, user added"}
                            onClick={() => props.onConfirmUserAssignment()}
                        />
                    </div>
                )}
            </>
        )}
        {
            (props.state === State.None) && (
                <>
                    <h1>Register your shop</h1>
                    <p>Quick and simple! Add a few shop details and get your own e-commerce site, plus mobile app integration where customers can discover, connect, and shop.</p>
                    <Row style={{ marginBottom: "2rem" }}>
                        <Col xs={2} style={{ textAlign: "center" }}>
                            <TbSquareNumber1 size={50} color={Color.DarkGrey} />
                        </Col>
                        <Col style={{ display: "flex", alignItems: "center" }}>
                            <span style={{ fontWeight: 600, verticalAlign: "middle", }}>Add your shop name</span>
                        </Col>
                    </Row>

                    <Row style={{ marginBottom: "2rem" }}>
                        <Col xs={2} style={{ textAlign: "center" }}>
                            <TbSquareNumber2 size={50} color={Color.DarkGrey} />
                        </Col>
                        <Col style={{ display: "flex", alignItems: "center" }}>
                            <span style={{ fontWeight: 600, verticalAlign: "middle", }}>Add your shop address</span>
                        </Col>
                    </Row>

                    <Row style={{ marginBottom: "2rem" }}>
                        <Col xs={2} style={{ textAlign: "center" }}>
                            <TbSquareNumber3 size={50} color={Color.DarkGrey} />
                        </Col>
                        <Col style={{ display: "flex", alignItems: "center" }}>
                            <span style={{ fontWeight: 600, verticalAlign: "middle", }}>Link your socials</span>
                        </Col>
                    </Row>

                    <Row style={{ marginBottom: "2rem" }}>
                        <Col xs={2} style={{ textAlign: "center" }}>
                            <TbSquareNumber4 size={50} color={Color.DarkGrey} />
                        </Col>
                        <Col style={{ display: "flex", alignItems: "center" }}>
                            <span style={{ fontWeight: 600, verticalAlign: "middle", }}>Upload your shop logo or image</span>
                        </Col>
                    </Row>

                    <Row style={{ marginBottom: "2rem" }}>
                        <Col xs={2} style={{ textAlign: "center" }}>
                            <TbSquareNumber5 size={50} color={Color.DarkGrey} />
                        </Col>
                        <Col style={{ display: "flex", alignItems: "center" }}>
                            <span style={{ fontWeight: 600, verticalAlign: "middle", }}>Set up online payments via our processor or offline through your own flow.</span>
                        </Col>
                    </Row>

                    <Button
                        style={{ width: "100%" }}
                        buttonvariant={variant.Primary}
                        name={"Register"}
                        onClick={() => props.onSubmit()} />
                </>
            )
        }
    </Form >
);
